import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, AlertCircle, Shield, KeyRound } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [userName, setUserName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await authApi.adminLogin(formData);

      // Gate 2FA forçado: admin sem 2FA cadastrado NÃO pode entrar.
      // Backend retorna setup_token curto (15min) pra ativar TOTP.
      if (data.requires_2fa_setup) {
        try {
          sessionStorage.setItem('force2faSetup', JSON.stringify({
            setup_token: data.setup_token,
            email: data.email,
            role: data.role,
            nome: data.nome,
          }));
        } catch { /* ignore */ }
        navigate('/admin/2fa-setup-obrigatorio', { state: data });
        return;
      }

      if (data.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(data.temp_token || data.challenge_token);
        setUserName(data.user?.nome || data.nome || '');
        setLoading(false);
        return;
      }

      login(data.access_token, data.user, data.refresh_token || null);
      localStorage.setItem('adminToken', data.access_token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      navigate('/admin');
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Acesso negado. Você não tem permissão de administrador.');
      } else if (err.response?.status === 401) {
        setError(err.response?.data?.detail || 'Email ou senha incorretos.');
      } else if (err.response?.status === 429) {
        setError(err.response?.data?.detail || 'Muitas tentativas. Tente novamente em alguns minutos.');
      } else {
        setError(err.response?.data?.detail || 'Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await authApi.admin2faVerify({ temp_token: tempToken, code: totpCode });
      login(data.access_token, data.user, data.refresh_token || null);
      localStorage.setItem('adminToken', data.access_token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }} />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
            Painel Administrativo
          </h1>
          <p className="text-purple-200">Empreende Comunidade</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {!needs2FA ? (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Acesso Restrito</h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="login-error-msg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" required value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      placeholder="seu@email.com" data-testid="admin-email-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="password" required value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      placeholder="••••••••" data-testid="admin-password-input" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="admin-login-btn">
                  {loading ? (
                    <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Entrando...</>
                  ) : (
                    <><Lock className="w-5 h-5" /> Entrar no Painel</>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl mb-3">
                  <KeyRound className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Verificacao 2FA</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Ola, {userName}! Digite o codigo do seu aplicativo autenticador.
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="2fa-error-msg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handle2FAVerify} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Codigo TOTP ou Backup</label>
                  <input
                    type="text"
                    required
                    autoFocus
                    maxLength={8}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\s/g, ''))}
                    className="w-full text-center text-2xl tracking-[0.5em] px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-mono"
                    placeholder="000000"
                    data-testid="2fa-code-input"
                  />
                </div>
                <button type="submit" disabled={loading || totpCode.length < 6}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="2fa-verify-btn">
                  {loading ? (
                    <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Verificando...</>
                  ) : (
                    <><KeyRound className="w-5 h-5" /> Verificar Codigo</>
                  )}
                </button>
                <button type="button" onClick={() => { setNeeds2FA(false); setError(''); setTotpCode(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 py-2" data-testid="2fa-back-btn">
                  Voltar para o login
                </button>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500">
              Acesso exclusivo para administradores e gestores tecnicos.
              <br />Se voce nao tem acesso, entre em contato com o suporte.
            </p>
          </div>
        </div>

        <p className="text-center text-purple-300 text-sm mt-6">
          &copy; 2026 Empreende Comunidade. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
